import { useMemo, useState } from 'react';

import { useSafeTrends, type SafeTrends, type TrendCategoryKey, type TrendSite } from './trendApi';

/**
 * 분석 › 연도별 추이 (사용자 결정 2026-10-28). 옛 안전보건관리시스템(2022~2026)과 우리 기록을 갈래마다 달별 막대로 쌓는다 —
 * 옛 기록은 옅은 회색, 우리 기록은 파랑, 우리 기록이 처음 선 달에 점선. 한 번 받아 두고 연도 칩은 화면에서 자른다.
 * 차트 라이브러리가 없어 SVG 로 그린다(SafeDashboardPage 의 Bars · Line 과 같은 결).
 */

const LEGACY_COLOR = '#cbd5e1';
const CURRENT_COLOR = '#2563eb';

/** 작은 차트로 그리는 갈래 — 시정조치 완료는 지적 차트에 선으로 겹친다. */
const CHART_KEYS: TrendCategoryKey[] = ['TBM', 'HEALTH', 'EDUCATION', 'INSPECTION', 'RISK', 'WORK', 'ACTION', 'VOICE'];

/** 현장 비교 표의 칸. 시정조치는 「지적 / 완료」 한 칸. */
const TABLE_KEYS: TrendCategoryKey[] = ['TBM', 'HEALTH', 'EDUCATION', 'INSPECTION', 'RISK', 'WORK', 'ACTION', 'VOICE'];

type Range = 'all' | string;

export function TrendSection() {
  const trends = useSafeTrends();
  const [range, setRange] = useState<Range>('all');

  if (!trends.data) {
    return (
      <div className="card" style={{ padding: '18px 20px', gridColumn: '1 / -1' }}>
        <div style={{ fontSize: '14px', fontWeight: 700 }}>연도별 추이</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>불러오는 중입니다</div>
      </div>
    );
  }
  return <TrendBody data={trends.data} range={range} onRange={setRange} />;
}

function TrendBody({ data, range, onRange }: { data: SafeTrends; range: Range; onRange: (range: Range) => void }) {
  const years = useMemo(() => Array.from(new Set(data.months.map((m) => m.slice(0, 4)))), [data.months]);
  const [start, end] = useMemo(() => {
    if (range === 'all') return [0, data.months.length];
    const first = data.months.findIndex((m) => m.startsWith(range));
    const last = data.months.length - [...data.months].reverse().findIndex((m) => m.startsWith(range));
    return first < 0 ? [0, data.months.length] : [first, last];
  }, [data.months, range]);
  const months = data.months.slice(start, end);
  const label = (key: TrendCategoryKey) => data.categories.find((c) => c.key === key)?.label ?? key;
  const series = (key: TrendCategoryKey) => data.series.find((s) => s.category === key);
  const meet = data.legacyLast && data.currentFirst ? months.indexOf(data.currentFirst) : -1;

  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <style>{TREND_STYLES}</style>
      <div className="card" style={{ padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700 }}>연도별 추이</div>
          <div className="sd-chips">
            {['all', ...years].map((key) => (
              <button key={key} type="button" className={range === key ? 'on' : ''} onClick={() => onRange(key)}>
                {key === 'all' ? '전체' : key}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <Swatch color={LEGACY_COLOR} text="기존 데이터" />
            <Swatch color={CURRENT_COLOR} text="지금" />
          </div>
        </div>
        <div className="sd-trend-grid">
          {CHART_KEYS.map((key) => {
            const s = series(key);
            if (!s) return null;
            const done = key === 'ACTION' ? series('ACTION_DONE') : undefined;
            const legacy = s.legacy.slice(start, end);
            const current = s.current.slice(start, end);
            const line = done ? sumArrays(done.legacy.slice(start, end), done.current.slice(start, end)) : undefined;
            const total = sum(legacy) + sum(current);
            return (
              <div key={key} className="sd-trend-cell">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 700 }}>{label(key)}</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {total.toLocaleString()}건{line ? ` · 완료 ${sum(line).toLocaleString()}건` : ''}
                  </span>
                </div>
                <MonthBars months={months} legacy={legacy} current={current} line={line} lineLabel="완료" meet={meet} />
              </div>
            );
          })}
        </div>
      </div>
      <SiteCompare data={data} label={label} />
    </div>
  );
}

function Swatch({ color, text }: { color: string; text: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: color, display: 'inline-block' }} />
      {text}
    </span>
  );
}

/**
 * 달마다 쌓은 막대(아래 옛 · 위 지금). {@code line} 이 있으면 같은 척도의 선을 겹친다(시정조치 완료).
 * {@code meet} 은 우리 기록이 처음 선 달 — 점선. 올리면 그 달의 값이 위에 뜬다.
 */
function MonthBars({
  months,
  legacy,
  current,
  line,
  lineLabel,
  meet,
}: {
  months: string[];
  legacy: number[];
  current: number[];
  line?: number[];
  lineLabel?: string;
  meet: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 360;
  const H = 150;
  const pad = { l: 34, r: 8, t: 18, b: 20 };
  const n = Math.max(1, months.length);
  const step = (W - pad.l - pad.r) / n;
  const barW = Math.max(1, step - (step > 6 ? 2 : 1));
  const old = (i: number) => legacy[i] ?? 0;
  const now = (i: number) => current[i] ?? 0;
  const top = niceMax(Math.max(1, ...months.map((_, i) => old(i) + now(i)), ...(line ?? [0])));
  const y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  const x = (i: number) => pad.l + i * step;
  const wide = months.length > 24;
  const at = hover;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onMouseLeave={() => setHover(null)}
      onMouseMove={(e) => {
        const box = e.currentTarget.getBoundingClientRect();
        const px = ((e.clientX - box.left) / box.width) * W;
        const i = Math.floor((px - pad.l) / step);
        setHover(i >= 0 && i < months.length ? i : null);
      }}
    >
      {[0, top / 2, top].map((v) => (
        <g key={v}>
          <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeWidth={1} />
          <text x={pad.l - 5} y={y(v) + 3.5} textAnchor="end" fontSize={9.5} fill="var(--text-muted)">
            {compact(v)}
          </text>
        </g>
      ))}
      {months.map((m, i) => {
        const show = wide ? m.endsWith('-01') : (Number(m.slice(5)) - 1) % 3 === 0;
        return show ? (
          <text key={m} x={x(i) + step / 2} y={H - 6} textAnchor={wide ? 'start' : 'middle'} fontSize={9.5} fill="var(--text-muted)">
            {wide ? m.slice(0, 4) : `${Number(m.slice(5))}월`}
          </text>
        ) : null;
      })}
      {months.map((m, i) => {
        const a = old(i);
        const b = now(i);
        const faded = hover !== null && hover !== i;
        return (
          <g key={m} opacity={faded ? 0.55 : 1}>
            {a > 0 && <rect x={x(i) + (step - barW) / 2} y={y(a)} width={barW} height={y(0) - y(a)} fill={LEGACY_COLOR} />}
            {b > 0 && <rect x={x(i) + (step - barW) / 2} y={y(a + b)} width={barW} height={y(a) - y(a + b)} fill={CURRENT_COLOR} />}
          </g>
        );
      })}
      {line && (
        <path
          d={line.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i) + step / 2},${y(v)}`).join(' ')}
          fill="none"
          stroke="var(--text-primary)"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
      )}
      {meet >= 0 && (
        <line x1={x(meet)} x2={x(meet)} y1={pad.t - 4} y2={y(0)} stroke="var(--text-secondary)" strokeWidth={1} strokeDasharray="3 3" />
      )}
      {at !== null && (
        <text
          x={Math.min(W - pad.r, Math.max(pad.l, x(at) + step / 2))}
          y={10}
          textAnchor={x(at) > W - 110 ? 'end' : x(at) < pad.l + 70 ? 'start' : 'middle'}
          fontSize={10.5}
          fontWeight={700}
          fill="var(--text-primary)"
        >
          {(months[at] ?? '').replace('-', '.')} · 기존 {old(at).toLocaleString()} · 지금 {now(at).toLocaleString()}
          {line ? ` · ${lineLabel ?? ''} ${(line[at] ?? 0).toLocaleString()}` : ''}
        </text>
      )}
    </svg>
  );
}

/** 현장 비교 — 줄은 현장(활동 많은 순 · 최대 20곳), 칸은 갈래. 「최근 12개월」 · 「전체」 를 칩으로 바꾼다. */
function SiteCompare({ data, label }: { data: SafeTrends; label: (key: TrendCategoryKey) => string }) {
  const [span, setSpan] = useState<'recent' | 'total'>('recent');
  const sites = data.sites ?? [];
  const value = (site: TrendSite, key: TrendCategoryKey) => (span === 'recent' ? site.recent : site.total)[key] ?? 0;
  const rows = [...sites].sort((a, b) =>
    span === 'recent' ? b.recentAll - a.recentAll || b.totalAll - a.totalAll : b.totalAll - a.totalAll,
  );

  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 700 }}>현장 비교</div>
        <div className="sd-chips">
          <button type="button" className={span === 'recent' ? 'on' : ''} onClick={() => setSpan('recent')}>
            최근 12개월
          </button>
          <button type="button" className={span === 'total' ? 'on' : ''} onClick={() => setSpan('total')}>
            전체
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px 0', textAlign: 'center' }}>없음</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="sd-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>현장</th>
                {TABLE_KEYS.map((key) => (
                  <th key={key}>{key === 'ACTION' ? '시정조치 지적 / 완료' : label(key)}</th>
                ))}
                <th>합계</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((site) => {
                const all = span === 'recent' ? site.recentAll : site.totalAll;
                return (
                  <tr key={site.siteId ?? `legacy:${site.siteName}`}>
                    <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                      {site.siteName}
                      {!site.siteId && (
                        <span className="badge badge-gray" style={{ marginLeft: '6px' }}>
                          기존 현장
                        </span>
                      )}
                    </td>
                    {TABLE_KEYS.map((key) => (
                      <td key={key} style={{ color: value(site, key) === 0 ? 'var(--text-muted)' : undefined }}>
                        {key === 'ACTION'
                          ? `${value(site, 'ACTION').toLocaleString()} / ${value(site, 'ACTION_DONE').toLocaleString()}`
                          : value(site, key).toLocaleString()}
                      </td>
                    ))}
                    <td style={{ fontWeight: 700, color: all === 0 ? 'var(--text-muted)' : undefined }}>{all.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}

function sumArrays(a: number[], b: number[]): number[] {
  return a.map((v, i) => v + (b[i] ?? 0));
}

/** 눈금 꼭대기 — 1 · 2 · 5 × 10ⁿ 로 올리고, 가운데 눈금이 정수가 되게 짝수로 맞춘다. */
function niceMax(value: number): number {
  if (value <= 2) return 2;
  const p = 10 ** Math.floor(Math.log10(value));
  const nice = [1, 2, 5, 10].map((m) => m * p).find((m) => m >= value) ?? 10 * p;
  return nice % 2 === 0 ? nice : nice + 1;
}

function compact(v: number): string {
  return v >= 10000 ? `${Math.round(v / 1000)}k` : v >= 1000 ? `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(v);
}

const TREND_STYLES = `
.sd-trend-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 18px 20px; }
.sd-trend-cell { min-width: 0; }
@media (max-width: 1280px) { .sd-trend-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 700px) { .sd-trend-grid { grid-template-columns: minmax(0, 1fr); } }
`;
